import { RadioGroupItem } from '@latitude/radio-button';
import { cn } from '@latitude/token-utils';
import type { MouseEvent, Ref } from 'react';
import { useCallback, useMemo } from 'react';

import { CardListItem } from './CardListItem';
import type { CardListItemRadioButtonProps } from './types';

/**
 * CardListItemRadioButton combines a CardListItem with a RadioButton in the leading slot.
 * This is a form-agnostic component that can be used with any form library.
 */
const CardListItemRadioButton = ({
  value,
  onValueChange,
  disabled,
  invalid,
  onBlur,
  radioTestId,
  className,
  title,
  subtitle,
  trailing,
  size,
  state,
  id,
  checked,
  hideRadioButton,
  ref,
}: CardListItemRadioButtonProps & {
  ref?: Ref<HTMLButtonElement>;
}) => {
  // Memoize the card state calculation based on radio button state and other conditions
  const cardState = useMemo((): CardListItemRadioButtonProps['state'] => {
    if (disabled) {
      return 'disabled';
    } else if (invalid) {
      return 'error';
    } else if (checked) {
      return 'selected';
    }
    return state || 'enabled';
  }, [disabled, invalid, checked, state]);

  const handleCardClick = useCallback(
    (e: MouseEvent) => {
      // Early return if disabled
      if (disabled) {
        return;
      }

      // If the target is a button, input, or contained within one, let that handle the click
      const target = e.target as HTMLElement;
      const isFormElement = target.closest('button, input, [role="radio"]');

      if (!isFormElement && onValueChange) {
        // Only if we're not clicking a form element, we manually set the value
        onValueChange(value);

        // We need to stop propagation to prevent any other handlers from firing
        e.stopPropagation();
      }
    },
    [disabled, onValueChange, value],
  );

  return (
    <div
      onClick={handleCardClick}
      tabIndex={-1}
      aria-disabled={disabled}
      aria-checked={checked ? 'true' : 'false'}
      className={cn('cursor-pointer', { 'cursor-not-allowed': disabled })}
    >
      <CardListItem
        className={className}
        leading={
          <RadioGroupItem
            ref={ref}
            id={id}
            value={value}
            disabled={disabled}
            data-testid={radioTestId}
            aria-invalid={invalid}
            onBlur={onBlur}
            hidden={hideRadioButton}
          />
        }
        title={title}
        subtitle={subtitle}
        trailing={trailing}
        size={size}
        state={cardState}
      />
    </div>
  );
};

CardListItemRadioButton.displayName = 'CardListItemRadioButton';

export { CardListItemRadioButton };
